import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeSection from "@/pages/home/components/SubscribeSection";
import { useSeo, SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-eye-line",
    title: "网站定位",
    desc: "不做八卦搬运，只做打榜环节的现象观察。我们把注意力放在票数怎么涨、规则怎么定、钱怎么花这些问题上。",
  },
  {
    icon: "ri-seedling-line",
    title: "发展理念",
    desc: "争议之所以反复出现，往往是因为数据不透明。我们的目标不是让热度更高，而是让讨论多一层可验证的依据。",
  },
  {
    icon: "ri-compass-3-line",
    title: "内容原则",
    desc: "区分事实与推断，不点名个人，不参与拉踩；当事方给出合理解释后，及时修订甚至更正原有结论。",
  },
  {
    icon: "ri-group-line",
    title: "读者共建",
    desc: "很多线索来自读者投稿。如果你手上有公开可核验的票数截图或账目记录，欢迎来信，我们会逐一查看。",
  },
];

const timeline = [
  { year: "起步", title: "从一条票数曲线开始", desc: "最早只是因为一张凌晨跳涨的截图，我们把分钟级数据整理成了第一份观察。" },
  { year: "扩展", title: "把打榜拆成五个栏目", desc: "从票数分析到粉丝生态，内容逐渐覆盖投票环节的完整链条。" },
  { year: "现在", title: "持续做可核验的记录", desc: "每篇内容都尽量标注时间、来源与推断边界，方便读者自行判断。" },
];

const testimonials = [
  {
    name: "赛事观众",
    text: "他们把「规则改了但争议没少」这件事讲透了，看完再刷榜单心态稳很多。",
  },
  {
    name: "粉丝后援成员",
    text: "关于集资透明的建议挺具体，我们后来真的照着改了公示方式。",
  },
  {
    name: "高校传播研究者",
    text: "作为打榜现象的观察样本，这些整理有明显的资料价值。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们｜瓜圈显微镜的内容原则与观察方法",
    description:
      "了解瓜圈显微镜：一个专注选美吃瓜投票打榜的内容观察站。我们公开网站定位、发展理念、内容原则与联系方式，说明如何围绕票数异常与榜单争议做出可核验的报道。",
    keywords: "关于瓜圈显微镜,选美吃瓜,选美吃瓜投票,内容原则",
    path: "/about",
    type: "profile",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        description: "瓜圈显微镜的网站定位、发展理念与内容原则。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: `${SITE_ORIGIN}/`,
        email: "editor@guaguan-microscope.example.com",
        description: "专注选美赛事投票打榜环节的内容观察站。",
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-foreground-950 text-background-100">
          <div className="px-4 md:px-6 xl:px-10 pt-8 pb-14 md:pb-20">
            <div className="max-w-[1320px] mx-auto">
              <Breadcrumbs tone="light" items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
                <div>
                  <p className="text-xs tracking-[0.3em] text-accent-400 mb-3">ABOUT US</p>
                  <h1 className="font-heading text-3xl md:text-4xl xl:text-5xl font-black leading-tight text-background-50">
                    我们是瓜圈显微镜
                  </h1>
                  <p className="mt-6 text-base leading-relaxed text-background-200 max-w-[620px]">
                    一个专注选美吃瓜与
                    <strong className="font-semibold text-background-50">投票打榜</strong>
                    的内容观察站。我们不靠抢热搜，而是把每一次票数异常、每一份集资公示、每一条计票规则，
                    尽可能地记录下来，让围观这件事变得更有依据。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden bg-background-950">
                  <div className="w-full aspect-[4/3]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Editorial%20flat%20lay%20of%20investigation%20notes%2C%20printed%20ranking%20charts%2C%20a%20magnifying%20glass%20and%20a%20small%20gold%20crown%20on%20a%20dark%20wooden%20desk%2C%20warm%20gold%20directional%20lighting%2C%20stylized%20premium%20magazine%20photography%2C%20no%20people&width=1200&height=900&seq=guaguan-about-desk&orientation=landscape"
                      alt="深色木桌上铺开的调查笔记、排名图表与小型金色皇冠"
                      title="关于瓜圈显微镜 - 选美吃瓜投票打榜观察"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="px-4 md:px-6 xl:px-10 py-14 md:py-20 scroll-mt-24">
          <div className="max-w-[1320px] mx-auto">
            <div className="max-w-[720px] mb-10">
              <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">WHAT WE STAND FOR</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                我们在意什么
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {values.map((item, index) => (
                <article
                  key={item.title}
                  className={[
                    "p-6 md:p-7 rounded-lg border",
                    index === 0
                      ? "bg-primary-500 border-primary-500"
                      : "bg-background-50 border-background-200",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "w-11 h-11 flex items-center justify-center rounded-md mb-5",
                      index === 0 ? "bg-background-50/20 text-background-50" : "bg-accent-100 text-accent-800",
                    ].join(" ")}
                  >
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3
                    className={[
                      "font-heading text-lg font-bold mb-2.5",
                      index === 0 ? "text-background-50" : "text-foreground-950",
                    ].join(" ")}
                  >
                    {item.title}
                  </h3>
                  <p
                    className={[
                      "text-sm leading-relaxed",
                      index === 0 ? "text-background-100" : "text-foreground-600",
                    ].join(" ")}
                  >
                    {item.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-y border-background-200">
          <div className="px-4 md:px-6 xl:px-10 py-14 md:py-20">
            <div className="max-w-[1320px] mx-auto">
              <div className="max-w-[720px] mb-10">
                <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">OUR STEPS</p>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  一路是怎么走过来的
                </h2>
              </div>
              <ol className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {timeline.map((item, index) => (
                  <li key={item.title} className="relative pl-6 border-l-2 border-accent-300">
                    <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-accent-500" />
                    <span className="text-xs tracking-widest text-accent-700">{item.year}</span>
                    <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    <span className="sr-only">第 {index + 1} 个阶段</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section id="voices" className="px-4 md:px-6 xl:px-10 py-14 md:py-20 scroll-mt-24">
          <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10">
            <div>
              <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">VOICES</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                读者怎么评价我们
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                这些评价来自读者来信与公开反馈。我们保留它们，是为了提醒自己别把观察写成站队。
              </p>
              <Link
                to="/ranking"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line" />
                去看看我们写了什么
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {testimonials.map((item) => (
                <blockquote
                  key={item.name}
                  className="bg-background-50 border border-background-200 rounded-lg p-6"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-500" />
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  <footer className="mt-4 text-xs text-foreground-500">— {item.name}</footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="px-4 md:px-6 xl:px-10 pb-14 md:pb-20 scroll-mt-24">
          <div className="max-w-[1320px] mx-auto bg-foreground-950 rounded-lg p-8 md:p-12 text-background-100">
            <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 items-center">
              <div>
                <p className="text-xs tracking-[0.3em] text-accent-400 mb-2">CONTACT</p>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  有线索，或者想指出我们的问题
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-background-300 max-w-[560px]">
                  欢迎提供可公开核验的票数截图、集资公示或规则说明。如果我们的某篇内容让你觉得判断失当，
                  也请直接来信，我们会认真读完。
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href="mailto:editor@guaguan-microscope.example.com"
                  rel="nofollow"
                  className="flex items-center gap-4 p-4 rounded-lg bg-foreground-900 hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-background-400">编辑部邮箱</span>
                    <span className="block text-sm text-background-50 truncate">
                      editor@guaguan-microscope.example.com
                    </span>
                  </span>
                </a>
                <Link
                  to="/ranking"
                  className="flex items-center gap-4 p-4 rounded-lg bg-foreground-900 hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950 shrink-0">
                    <i className="ri-radar-line text-lg" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-background-400">内容入口</span>
                    <span className="block text-sm text-background-50">打榜观察栏目</span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <SubscribeSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}